<script setup>
import { useStore } from 'vuex'
import DataTableField from '@/components/form/DataTableField.vue'
import TableActionMenu from '@/components/common/TableActionMenu.vue'
import FilterChipGroup from '@/components/boundary-condition/FilterChipGroup.vue'
import DropDownSingleSelect from '@/components/form/DropDownSingleSelect.vue'
import { computed, onMounted, ref, toRef, watch } from 'vue'
import { actionMenuMixin } from '@/mixins/actionMenuMixin'
import { dirtyFieldMixin } from '@/mixins/dirtyFieldMixin'
import { boundaryConditionTableMixin } from '@/mixins/boundaryConditionTableMixin'

const STORE_NAMESPACE = 'riverWaterLevel'
const YEAR_COLSPAN = 7
const HEADER = [
  {
    title: 'Extreme',
    value: 'river_water_level_extreme',
    class: 'pl-1 pl-1'
  },
  {
    title: 'High',
    value: 'river_water_level_high',
    field: true,
    class: 'pl-1 pl-1'
  },
  {
    title: 'Daily',
    value: 'river_water_level_daily',
    field: true,
    class: 'pl-1 pl-1'
  },
  {
    title: 'Low',
    value: 'river_water_level_low',
    field: true,
    class: 'pl-1 pl-1'
  },
  {
    title: 'High',
    value: 'polder_water_level_high',
    field: true,
    class: 'pl-1 pl-1'
  },
  {
    title: 'Daily',
    value: 'polder_water_level_daily',
    field: true,
    class: 'pl-1 pl-1'
  },
  {
    title: 'Low',
    value: 'polder_water_level_low',
    field: true,
    class: 'pl-1 pl-1'
  }
]

const store = useStore()

const props = defineProps({
  items: { type: Array, required: true },
  years: { type: Array, required: true },
  count: { type: Number, require: true },
  editable: { require: false, default: true }
})

const items = toRef(props, 'items')

const { x, y, showMenu, selectedItem, show } = actionMenuMixin()
const { dirtyItems, addDirty, getDirtyClass } = dirtyFieldMixin()

const deleteFromState = (item) => {
  store.dispatch(`${STORE_NAMESPACE}/deleteFromState`, item)
}

const deleteItems = (input) => {
  store.dispatch(`${STORE_NAMESPACE}/deleteByReferencePoint`, input)
}

const emit = defineEmits(['changePage'])

const {
  itemPerPage,
  referencePointsOptions,
  referencePointsShow,
  activeYears,
  filterYearId,
  borderBottom,
  updateOptions,
  deleteReferencePoint
} = boundaryConditionTableMixin(
  STORE_NAMESPACE,
  items,
  selectedItem,
  getDirtyClass,
  deleteItems,
  deleteFromState,
  emit
)

const riverWaterLevelHeader = ref([])
const loading = computed(() => store.state.riverWaterLevel.loading)

watch(
  activeYears,
  () => {
    updateRiverWaterLevelHeader()
  },
  { deep: true }
)
watch(
  () => props.years,
  () => {
    activeYears.value = JSON.parse(JSON.stringify(props.years))
  }
)

const setReferencePoint = (input) => {
  store.dispatch(`${STORE_NAMESPACE}/setReferencePoint`, input)
}
const setParameter = (input) => {
  store.dispatch(`${STORE_NAMESPACE}/setParameter`, input)
}
const updateRiverWaterLevelHeader = () => {
  if (activeYears.value) {
    let updatedRiverWaterLevelHeader = []
    const duplicationAmount = activeYears.value ? activeYears.value.length : 0
    for (let i = 0; i < duplicationAmount; i++) {
      updatedRiverWaterLevelHeader.push(...HEADER)
    }
    riverWaterLevelHeader.value = updatedRiverWaterLevelHeader
  }
}
const createFilters = () => {
  activeYears.value = JSON.parse(JSON.stringify(props.years))
}
defineExpose({
  dirtyItems
})

onMounted(() => {
  createFilters()
  updateRiverWaterLevelHeader()
})
</script>

<template>
  <div>
    <div style="justify-content: space-between; display: flex">
      <v-card variant="flat" v-if="years.length">
        <FilterChipGroup :items="years" :active-items="activeYears" parameter="year" />
      </v-card>
      <slot></slot>
    </div>
    <v-row>
      <v-col>
        <v-data-table-server
          class="data-table"
          :items-length="count ?? 0"
          :items="referencePointsShow"
          density="compact"
          hide-no-data
          :items-per-page="itemPerPage"
          @update:options="updateOptions"
          :footer-props="{
            disableItemsPerPage: true
          }"
        >
          <template #headers>
            <tr>
              <th></th>
              <th
                v-for="(year, index) in activeYears"
                :key="`year-${year.id}-${index}`"
                :colspan="YEAR_COLSPAN"
                :style="filterYearId(year.id)"
              >
                {{ year.year }}
              </th>
            </tr>
            <tr>
              <th></th>
              <template v-for="year in activeYears" :key="`river_water_level_${year.id}`">
                <th :colspan="4" :style="filterYearId(year.id)">
                  <span class="pl-1">River Water Level</span>
                </th>
                <th :colspan="3" :style="filterYearId(year.id)">
                  <span class="pl-1">Polder Water Level</span>
                </th>
              </template>
            </tr>
            <tr>
              <th style="min-width: 200px">
                <span class="pl-1">Reference point</span>
              </th>
              <th v-for="(header, idx) in riverWaterLevelHeader" :key="`header-${idx}`">
                <span class="pl-1"> {{ header.title }} </span>
              </th>
            </tr>
          </template>
          <template #body>
            <tr v-if="loading" class="v-data-table-progress">
              <th colspan="999" style="text-align: center">
                <v-progress-linear indeterminate color="primary" />
              </th>
            </tr>
            <template v-else>
              <tr
                v-for="item in referencePointsShow"
                :key="item.id"
                @contextmenu="show($event, item)"
              >
                <td>
                  <DropDownSingleSelect
                    :options="referencePointsOptions"
                    :class="`${borderBottom(item)}`"
                    :editable="editable"
                    :item="item.id"
                    @addDirty="addDirty(item, item.id)"
                    @update="setReferencePoint"
                  />
                </td>

                <template v-for="yearItem in item.riverwaterlevel_set">
                  <td
                    v-for="header in HEADER"
                    :key="`${yearItem.id}-${header.value}`"
                    :style="filterYearId(yearItem.year)"
                  >
                    <DataTableField
                      :class="getDirtyClass(item, header.value + yearItem.id)"
                      :editable="editable"
                      :item="yearItem"
                      :parameter="header.value"
                      width="75px"
                      @set="setParameter"
                      @update="addDirty(item, header.value + yearItem.id)"
                    />
                  </td>
                </template>
              </tr>
            </template>
          </template>
        </v-data-table-server>
      </v-col>
    </v-row>
    <TableActionMenu
      v-if="editable"
      :add-button="false"
      :selected-item="selectedItem"
      :show-menu="showMenu"
      :x="x"
      @update:show-menu="showMenu = $event"
      :y="y"
      @action="deleteReferencePoint()"
    />
  </div>
</template>
<style scoped>
.v-table :deep(th) {
  border-bottom: 1px solid rgba(255, 255, 255, 0) !important;
  text-align: left;
  padding: 0 !important;
  font-size: 0.75rem;
  font-family: Roboto, sans-serif;
  font-weight: bold !important;
  color: rgba(0, 0, 0, 0.6);
}
.v-table :deep(td) {
  padding: 0 !important;
}

.v-table:deep(th) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
}
</style>
